import { useForm } from "react-hook-form";
import { Box, Stack } from "@mui/material";
import { useNavigate } from "react-router-dom";
import Text from "src/components/Displays/Text";
import { useTranslation } from "react-i18next";
import { useContext, useEffect, useState } from "react";
import BackButton from "src/components/Shared/BackButton";
import { ThemeValues } from "src/components/ThemeUsage/theme";
import SubmitButton from "src/components/Shared/SubmitButton";
import { ThemeContext } from "src/components/ThemeUsage/ThemeUsage";
import { SMART_CHARGING_ROUTES } from "src/consts/Paths/SmartCharging";
import ConnectConfirmForm from "src/components/Forms/SmartCharging/ConnectConfirmForm";

export interface ConnectConfirmFormValues {
  wallboxConfigured: boolean;
  wallboxRestarted: boolean;
}

const ConnectConfirm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const themeName = useContext(ThemeContext);

  const [formSubmitDisabled, setFormSubmitDisabled] = useState(true);

  const form = useForm<ConnectConfirmFormValues>({
    defaultValues: {
      wallboxConfigured: false,
      wallboxRestarted: false,
    },
  });

  useEffect(() => {
    const subscription = form.watch((value, { name, type }) => {
      if (name === "wallboxConfigured" || name === "wallboxRestarted") {
        if (
          form.getValues().wallboxConfigured === true &&
          form.getValues().wallboxRestarted === true
        ) {
          setFormSubmitDisabled(false);
        } else {
          setFormSubmitDisabled(true);
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [form]);

  const onSubmit = async (data: ConnectConfirmFormValues) => {
    navigate(SMART_CHARGING_ROUTES.CONNECTING);
  };

  return (
    <>
      <Stack gap={5}>
        <Stack direction={"row"} alignItems={"center"} gap={1}>
          <BackButton
            variant={
              themeName === ThemeValues.lichtblick ||
              themeName === ThemeValues.SMA
                ? "dark"
                : "light"
            }
            onClick={() => navigate(-1)}
            sx={(theme) => ({
              backgroundColor: theme.palette.background.default,
              "&:hover": {
                backgroundColor: theme.palette.background.default,
              },
              display: { xs: "none", sm: "flex" },
            })}
          />
          <Text
            text={t("page.wallboxConnectConfirm.pageTitle")}
            sx={(theme) => ({
              fontWeight: 500,
              fontSize: "32px",
              lineHeight: "40px",
              fontFamily: "officina",
              color: theme.palette.mainFont.primary,
            })}
          />
        </Stack>

        <Text
          text={t("page.wallboxConnectConfirm.pageSubTitle1")}
          sx={(theme) => ({
            fontSize: "14px",
            fontWeight: "400",
            lineHeight: "20px",
            fontFamily: "Inter",
            color: theme.palette.mainFont.primary,
          })}
        />

        <Box sx={{ maxWidth: { xs: "100%", sm: "380px" } }}>
          <Box
            component={"form"}
            autoComplete="off"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <Stack gap={5}>
              <ConnectConfirmForm form={form} />
              <Box>
                <SubmitButton
                  type="submit"
                  disabled={formSubmitDisabled}
                  buttonText={t("page.wallboxConnectConfirm.nextButton")}
                  sx={(theme) => ({
                    fontSize: "14px",
                    lineHeight: "20px",
                    textTransform: "none",
                    color: theme.palette.mainFont.primary,
                    "&:disabled": {
                      opacity: 0.1,
                      bgcolor: "#000",
                      fontSize: "14px",
                      lineHeight: "20px",
                      cursor: "not-allowed",
                      pointerEvents: "all !important",
                    },
                  })}
                />
              </Box>
            </Stack>
          </Box>
        </Box>
      </Stack>
    </>
  );
};

export default ConnectConfirm;
